import { useState, useEffect, useCallback } from 'react';
import { Row, Col, Empty, Spin, Typography, Space, List, Card, Image, Pagination, Select, Button, Badge, Tag } from 'antd';
import { ProductSearch } from '../components/products/ProductSearch';
import { ProductCard } from '../components/products/ProductCard';
import { ViewToggle, ViewMode } from '../components/products/ViewToggle';
import { QualityScoreBadge } from '../components/common/QualityScoreBadge';
import { ProviderTag } from '../components/common/ProviderTag';
import { useProducts } from '../hooks/useProducts';
import { SearchType, Product } from '../types/product';
import { DataSourceFilter } from '../api/products';
import { useNavigate, useSearchParams } from 'react-router-dom';

const { Title, Text } = Typography;

const PAGE_SIZE_OPTIONS = [
  { value: 20, label: '20 per page' },
  { value: 50, label: '50 per page' },
  { value: 100, label: '100 per page' },
  { value: 200, label: '200 per page' },
];

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All Sources' },
  { value: 'pim', label: 'PIM' },
  { value: 'etilize', label: 'NIQ Brandbank' },
  { value: 'hp', label: 'HP' },
  { value: 'external', label: 'CSV Imports' },
  { value: 'icecat', label: 'IceCat' },
];

// Fetch a larger batch from the backend for client-side pagination
const FETCH_SIZE = 500;

export function ProductListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize from URL query parameters
  const urlSearchType = (searchParams.get('type') as SearchType) || 'mpn';
  const urlSearchQuery = searchParams.get('q') || '';
  const urlSource = (searchParams.get('source') as DataSourceFilter) || 'pim';
  const urlExactMatch = searchParams.get('exact') === '1';

  const [searchType, setSearchType] = useState<SearchType>(urlSearchType);
  const [searchQuery, setSearchQuery] = useState(urlSearchQuery);       // committed query (triggers API)
  const [inputValue, setInputValue] = useState(urlSearchQuery);          // live input value (no API call)
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [source, setSource] = useState<DataSourceFilter>(urlSource);
  const [exactMatch, setExactMatch] = useState<boolean>(urlExactMatch);
  // "Search online": opt into a live external-provider lookup for the current
  // query. Reset on every new/changed search so it's always a deliberate action.
  const [online, setOnline] = useState(false);

  // Sync URL params to state when URL changes (browser back/forward)
  useEffect(() => {
    const newType = (searchParams.get('type') as SearchType) || 'mpn';
    const newQuery = searchParams.get('q') || '';
    const newSource = (searchParams.get('source') as DataSourceFilter) || 'pim';
    const newExact = searchParams.get('exact') === '1';

    setSearchType(newType);
    setSearchQuery(newQuery);
    setInputValue(newQuery);
    setSource(newSource);
    setExactMatch(newExact);
    setOnline(false);
    setCurrentPage(1);
  }, [searchParams]);

  // Update URL when search parameters change
  const updateUrlParams = useCallback((
    type: SearchType,
    query: string,
    src: DataSourceFilter,
    exact: boolean,
  ) => {
    const params = new URLSearchParams();
    if (type !== 'mpn') params.set('type', type);
    if (query) params.set('q', query);
    if (src !== 'pim') params.set('source', src);
    if (exact && type === 'mpn') params.set('exact', '1');

    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const { data: products, isLoading, isError, error } = useProducts({
    searchType,
    query: searchQuery,
    pageSize: FETCH_SIZE, // Always fetch large batch for client-side pagination
    source,
    exactMatch: searchType === 'mpn' ? exactMatch : undefined,
    online,
  });

  // Client-side pagination for displayed products
  const paginatedProducts = products?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  ) || [];
  const totalProducts = products?.length || 0;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  const handleSearch = (query: string) => {
    setInputValue(query);
    setSearchQuery(query);  // commit to trigger API
    setOnline(false);       // a new search starts local-first
    setCurrentPage(1);
    updateUrlParams(searchType, query, source, exactMatch);
  };

  const handleSearchTypeChange = (type: SearchType) => {
    setSearchType(type);
    // Update URL with new type if there's an active search
    if (searchQuery) {
      updateUrlParams(type, searchQuery, source, exactMatch);
    }
  };

  const handleSourceChange = (newSource: DataSourceFilter) => {
    setSource(newSource);
    setCurrentPage(1);
    updateUrlParams(searchType, searchQuery, newSource, exactMatch);
  };

  const handleExactMatchChange = (exact: boolean) => {
    setExactMatch(exact);
    setCurrentPage(1);
    if (searchQuery) {
      updateUrlParams(searchType, searchQuery, source, exact);
    }
  };

  const handleProductClick = (product: Product) => {
    navigate(`/products/${encodeURIComponent(product.productid)}`, {
      state: { product },
    });
  };

  // Identifier searches can be retried as an explicit live lookup; description/
  // category searches have no live equivalent at the providers.
  const canSearchOnline = ['mpn', 'sku', 'upc'].includes(searchType);

  const renderEmpty = () => {
    if (!searchQuery) {
      return (
        <Empty description="No products imported yet. Import products using the Import page." />
      );
    }
    if (online) {
      return <Empty description="No matching products found from external providers." />;
    }
    return (
      <Empty description="No products in your catalog match this search.">
        {canSearchOnline && (
          <Space direction="vertical" size={4}>
            <Button type="primary" onClick={() => setOnline(true)}>
              Search external providers
            </Button>
            <Text type="secondary">Live lookup (may take a few seconds)</Text>
          </Space>
        )}
      </Empty>
    );
  };

  const renderGridView = () => {
    if (!products || products.length === 0) {
      return renderEmpty();
    }

    return (
      <Row gutter={[16, 16]}>
        {paginatedProducts.map((product) => (
          <Col key={product.productid} xs={24} sm={12} md={8} lg={6} xl={4}>
            {product._new ? (
              <Badge.Ribbon text="New" color="green">
                <ProductCard product={product} />
              </Badge.Ribbon>
            ) : (
              <ProductCard product={product} />
            )}
          </Col>
        ))}
      </Row>
    );
  };

  const renderListView = () => {
    if (!products || products.length === 0) {
      return renderEmpty();
    }

    return (
      <List
        itemLayout="horizontal"
        dataSource={paginatedProducts}
        renderItem={(product) => {
          const primaryImage = product.elements?.find(
            (el) => el.type === 'Image'
          );
          // Handle both string and object formats for product.image
          let imageUrl: string | undefined = undefined;
          if (primaryImage?.url) {
            imageUrl = primaryImage.url;
          } else if (product.image) {
            if (typeof product.image === 'string') {
              imageUrl = product.image;
            } else if (typeof product.image === 'object') {
              const img = product.image as { original?: string; '500'?: string; '300'?: string };
              imageUrl = img.original || img['500'] || img['300'];
            }
          }
          const productName =
            product.description ||
            product.mfgpartno ||
            product.productid;

          return (
            <Card
              hoverable
              style={{ marginBottom: 8 }}
              onClick={() => handleProductClick(product)}
            >
              <div style={{ display: 'flex', gap: 16 }}>
                <div
                  style={{
                    width: 100,
                    height: 100,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f5f5f5',
                    borderRadius: 4,
                  }}
                >
                  <Image
                    src={imageUrl}
                    alt={productName}
                    style={{ maxWidth: 90, maxHeight: 90, objectFit: 'contain' }}
                    preview={false}
                    fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Crect fill='%23f0f0f0' width='80' height='80'/%3E%3Ctext x='40' y='40' font-family='Arial' font-size='10' fill='%23999' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E"
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Space style={{ marginBottom: 4 }}>
                    {product._new && <Tag color="green">New</Tag>}
                    <ProviderTag provider={product.data_source} />
                    <QualityScoreBadge score={product.quality_score} />
                  </Space>
                  <Title level={5} ellipsis style={{ margin: '4px 0' }}>
                    {productName}
                  </Title>
                  <Space split={<Text type="secondary">|</Text>}>
                    {product.manufacturer?.name && (
                      <Text type="secondary">{product.manufacturer.name}</Text>
                    )}
                    {product.mfgpartno && (
                      <Text type="secondary">MPN: {product.mfgpartno}</Text>
                    )}
                    {product._alternatives && product._alternatives.length > 0 && (
                      <Text type="secondary">
                        +{product._alternatives.length} alternative
                        {product._alternatives.length > 1 ? 's' : ''}
                      </Text>
                    )}
                  </Space>
                </div>
              </div>
            </Card>
          );
        }}
      />
    );
  };

  return (
    <div>
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <ProductSearch
          searchType={searchType}
          onSearchTypeChange={handleSearchTypeChange}
          onSearch={handleSearch}
          loading={isLoading}
          value={inputValue}
          onChange={setInputValue}
          exactMatch={exactMatch}
          onExactMatchChange={handleExactMatchChange}
        />
        <Space>
          {totalProducts > 0 && (
            <Text type="secondary">
              {totalProducts} result{totalProducts !== 1 ? 's' : ''}
            </Text>
          )}
          <Select
            value={source}
            onChange={handleSourceChange}
            options={SOURCE_OPTIONS}
            style={{ width: 140 }}
          />
          <Select
            value={pageSize}
            onChange={handlePageSizeChange}
            options={PAGE_SIZE_OPTIONS}
            style={{ width: 130 }}
          />
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </Space>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Searching products...</Text>
          </div>
        </div>
      ) : isError ? (
        <Empty
          description={
            <span>
              Error loading products:{' '}
              {error instanceof Error ? error.message : 'Unknown error'}
            </span>
          }
        />
      ) : viewMode === 'grid' ? (
        renderGridView()
      ) : (
        renderListView()
      )}

      {/* Pagination */}
      {totalProducts > 0 && (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Pagination
            current={currentPage}
            total={totalProducts}
            pageSize={pageSize}
            onChange={handlePageChange}
            showSizeChanger={false}
            showQuickJumper={totalProducts > 100}
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} products`
            }
          />
        </div>
      )}

      {/* Offer a live lookup even when local results exist, in case the exact
          product being searched for hasn't been discovered yet. */}
      {!isLoading && searchQuery && canSearchOnline && totalProducts > 0 && (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          {online ? (
            <Text type="secondary">External provider results included.</Text>
          ) : (
            <Space direction="vertical" size={2}>
              <Text type="secondary">Not finding what you're looking for?</Text>
              <Button onClick={() => setOnline(true)}>
                Search external providers
              </Button>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Live lookup (may take a few seconds)
              </Text>
            </Space>
          )}
        </div>
      )}
    </div>
  );
}
